import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { touch, remembered, tokenIssuedAt, LIMITS, endSession } from "@/lib/session";
import { clearSWCaches } from "@/lib/pwa";
import { disablePush } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("os_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [tenantId, setTenantIdState] = useState(() => localStorage.getItem("os_tenant") || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("os_token");
    if (token) {
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        localStorage.setItem("os_user", JSON.stringify(r.data));
      }).catch(() => {});
    }
    const refresh = () => {
      if (!localStorage.getItem("os_token") || !remembered()) return;
      if (Date.now() - tokenIssuedAt() > LIMITS.refreshAfter) api.post("/auth/refresh").then((r) => localStorage.setItem("os_token", r.data.access_token)).catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const login = async (email, password, remember = false) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password, remember });
      localStorage.setItem("os_token", r.data.access_token);
      localStorage.setItem("os_remember", remember ? "1" : "0");
      touch();
      localStorage.setItem("os_user", JSON.stringify(r.data.user));
      if (r.data.user.role !== "SUPER_ADMIN") {
        localStorage.removeItem("os_tenant");
        setTenantIdState(null);
      }
      setUser(r.data.user);
      return r.data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await Promise.race([disablePush(), new Promise((r) => setTimeout(r, 1500))]); // unbind push from this account/device
    setUser(null);
    setTenantIdState(null);
    clearSWCaches();
    endSession(); // clears storage and replaces history so "Voltar" never shows authenticated screens
  };

  const setTenantId = (id) => {
    if (id) localStorage.setItem("os_tenant", id);
    else localStorage.removeItem("os_tenant");
    setTenantIdState(id || null);
  };

  const isSuper = user?.role === "SUPER_ADMIN";
  const isAdmin = isSuper || user?.role === "TENANT_ADMIN";
  const hasPerm = (...perms) => isAdmin || perms.some((p) => user?.permissions?.includes(p));
  const activeTenant = isSuper ? tenantId : user?.tenant_id;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, tenantId, setTenantId, isSuper, isAdmin, hasPerm, activeTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
